import { useState, useEffect } from 'react';
import { Mail, RefreshCw, TrendingUp, AlertCircle } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import EmailHistoryTable from '../../components/emails/EmailHistoryTable';
import EmailDetailModal from '../../components/emails/EmailDetailModal';
import emailsApi from '../../api/emails';
import { useUIStore } from '../../stores/uiStore';

/**
 * EmailHistory Page
 * Main page for viewing and managing email history
 */
const EmailHistory = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({});

  const showToast = useUIStore(state => state.showToast);

  // Load email history
  const loadEmails = async (page = 1, newFilters = filters) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pagination.limit,
        ...newFilters
      };

      const response = await emailsApi.getHistory(params);
      setEmails(response.data.emails);
      setPagination(response.data.pagination);
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to load email history', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load email statistics
  const loadStats = async () => {
    try {
      const response = await emailsApi.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load email stats:', error);
    }
  };

  // Initial load
  useEffect(() => {
    loadEmails();
    loadStats();
  }, []);

  // Handle page change
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
    loadEmails(page);
  };

  // Handle filter change
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    loadEmails(1, newFilters);
  };

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadEmails(pagination.page), loadStats()]);
    setRefreshing(false);
    showToast('Email history refreshed', 'success');
  };

  // Handle view details
  const handleViewDetails = async (email) => {
    try {
      const response = await emailsApi.getById(email.id);
      setSelectedEmail(response.data);
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to load email details', 'error');
    }
  };

  // Handle retry
  const handleRetry = async (emailId) => {
    try {
      await emailsApi.retry(emailId);
      showToast('Email queued for retry', 'success');
      loadEmails(pagination.page);
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to retry email', 'error');
    }
  };

  // Handle retry success from modal
  const handleRetrySuccess = () => {
    loadEmails(pagination.page);
    loadStats();
  };

  // Calculate success rate
  const getSuccessRate = () => {
    if (!stats?.overall) return '0.0';
    const total = stats.overall.total_sent || 0;
    const successful = stats.overall.successful || 0;
    if (total === 0) return '0.0';
    return ((successful / total) * 100).toFixed(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email History</h1>
          <p className="text-gray-600 mt-1">
            View and manage all sent emails, track delivery status, and retry failed emails
          </p>
        </div>
        <Button
          variant="primary"
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Sent</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.overall?.total_sent || 0}
                </p>
              </div>
              <Mail className="h-8 w-8 text-blue-500" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Successful</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {stats.overall?.successful || 0}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Failed</p>
                <p className="text-2xl font-bold text-red-600 mt-1">
                  {stats.overall?.failed || 0}
                </p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Success Rate</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {getSuccessRate()}%
                </p>
              </div>
              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 font-bold">%</span>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Provider Statistics */}
      {stats?.by_provider && stats.by_provider.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">By Provider</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {stats.by_provider.map((provider) => (
              <div key={provider.provider} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900 uppercase">
                    {provider.provider}
                  </span>
                  <span className="text-sm text-gray-600">
                    {provider.total_sent > 0
                      ? ((provider.successful / provider.total_sent) * 100).toFixed(1)
                      : '0.0'}% success
                  </span>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total:</span>
                    <span className="font-medium">{provider.total_sent || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Successful:</span>
                    <span className="font-medium text-green-600">{provider.successful || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Failed:</span>
                    <span className="font-medium text-red-600">{provider.failed || 0}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Email Type Statistics */}
      {stats?.by_type && stats.by_type.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">By Email Type</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {stats.by_type.map((type) => (
              <div key={type.email_type} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900 capitalize">
                    {type.email_type?.replace(/_/g, ' ') || 'Unknown'}
                  </span>
                  <span className="text-sm text-gray-600">
                    {type.total_sent > 0
                      ? ((type.successful / type.total_sent) * 100).toFixed(1)
                      : '0.0'}%
                  </span>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total:</span>
                    <span className="font-medium">{type.total_sent || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Success:</span>
                    <span className="font-medium text-green-600">{type.successful || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Failed:</span>
                    <span className="font-medium text-red-600">{type.failed || 0}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Email History Table */}
      <EmailHistoryTable
        emails={emails}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onViewDetails={handleViewDetails}
        onRetry={handleRetry}
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      {/* Email Detail Modal */}
      {selectedEmail && (
        <EmailDetailModal
          email={selectedEmail}
          onClose={() => setSelectedEmail(null)}
          onRetrySuccess={handleRetrySuccess}
        />
      )}
    </div>
  );
};

export default EmailHistory;